import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { PreSelectSingle } from '../common/form/PreselectSingle';
import { supplierCarService } from '@api/services/services';
import { SupplierCarTypeDto } from '@api/models/suppliers/SupplierCarTypeDto';
import { getLanguageVariant, getLanguageVariantName } from '../../utils/format';

interface Props {
  name: string;
  label?: React.ReactNode;
  required?: boolean;
  fullWidth?: boolean;
  refreshTrigger?: number;
}

export const SupplierCarTypeSelect: FC<Props> = ({ name, label, required, fullWidth, refreshTrigger }) => {
  const { i18n } = useTranslation();
  const { values, setFieldValue } = useFormikContext<any>();
  const [carTypes, setCarTypes] = useState<SupplierCarTypeDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCarTypes = async () => {
      try {
        setLoading(true);
        const response = await supplierCarService.getAllCarTypesByCompany();
        const carTypesWithName = response.data.map((carType) => ({
          ...carType,
          name: getLanguageVariant(carType.languageVariants, i18n.language)?.name,
        }));
        setCarTypes(carTypesWithName);
      } catch (error) {
        console.error('Failed to fetch car types:', error);
        setCarTypes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCarTypes();
  }, [i18n.language, refreshTrigger]);

  useEffect(() => {
    const currentValue = values[name];
    if (!currentValue || carTypes.length === 0) return;

    const isStillAvailable = carTypes.some((carType) => carType.id === currentValue);
    if (!isStillAvailable) setFieldValue(name, '');
  }, [carTypes]);

  return (
    <PreSelectSingle
      name={name}
      label={label}
      required={required}
      options={carTypes}
      loading={loading}
      getOptionId={(option: SupplierCarTypeDto) => option.id || ''}
      getOptionName={(option: SupplierCarTypeDto) => getLanguageVariantName(option.languageVariants, i18n.language)}
      getOptionNameFromId={(id: string) => {
        const carType = carTypes.find((type) => type.id === id);
        return getLanguageVariantName(carType?.languageVariants, i18n.language);
      }}
      fullWidth={fullWidth}
    />
  );
};
